import { palette } from './palette';

/**
 * How this theme ships CSS.
 *
 * <p>The storefront's Tailwind build only scans its own sources, so utility
 * classes written here would never be generated. The platform blocks solve the
 * same problem with inline styles plus a scoped `<style>` element (see the
 * generic `FacetPanel`). This theme does the same, with one refinement: it uses
 * React 19's hoistable `<style href precedence>`, so each stylesheet is emitted
 * ONCE per document however many blocks use it, and lands in `<head>`.
 *
 * <p>Every rule is scoped under a `sk-` class, so nothing leaks onto the
 * platform's own blocks or chrome.
 */
export function SkStyles({ id, css }: { id: string; css: string }) {
  return (
    <style href={`spatial-kitchen-${id}`} precedence="spatial-kitchen">
      {css}
    </style>
  );
}

/**
 * Base layer: the scope variables every block reads, plus the shared type,
 * button, link, focus and image-mask primitives.
 *
 * <p>Each `--sk-*` variable reads the tenant's design token first and falls
 * back to the brief's literal, so the page looks right even on a route whose
 * payload carries no tokens.
 */
export const BASE_CSS = `
.sk{
  --sk-ink:var(--color-text-primary,${palette.ink});
  --sk-muted:var(--color-text-secondary,${palette.muted});
  --sk-sage:var(--color-brand-500,${palette.sage});
  --sk-sage-deep:var(--color-brand-600,${palette.sageDeep});
  --sk-stone:var(--color-accent-300,${palette.stone});
  --sk-stone-deep:var(--color-accent-400,${palette.stoneDeep});
  --sk-sand:var(--color-sand-500,${palette.sand});
  --sk-brass-text:var(--color-sand-700,${palette.brassText});
  --sk-brass-soft:var(--color-sand-100,${palette.brassSoft});
  --sk-clay:var(--color-clay-600,${palette.clay});
  --sk-faint:var(--color-text-muted,${palette.faint});
  --sk-white:var(--color-surface-card,${palette.white});
  --sk-warm:var(--color-surface-subtle,${palette.warm});
  --sk-sage-grey:var(--color-surface-muted,${palette.sageGrey});
  --sk-border:var(--color-border-default,${palette.border});
  --sk-control-border:var(--color-border-strong,${palette.controlBorder});
  --sk-focus:var(--color-border-focus,${palette.sageDeep});
  --sk-serif:var(--font-family-display,'Fraunces','Iowan Old Style','Palatino Linotype',Georgia,serif);
  --sk-sans:var(--font-family-base,'Manrope',system-ui,-apple-system,'Segoe UI',Roboto,sans-serif);
  --sk-gutter:clamp(1rem,2.5vw + .25rem,2rem);
  --sk-max:var(--app-max-width,1240px);
  --sk-max-wide:var(--app-max-width-wide,1440px);
  --sk-radius-button:var(--radius-button,999px);
  --sk-radius-card:var(--radius-card,14px);
  --sk-radius-sm:var(--radius-sm,9px);
  --sk-shadow:0 1px 2px rgba(29,33,31,.05),0 8px 24px rgba(29,33,31,.06);
  --sk-shadow-lg:0 2px 6px rgba(29,33,31,.06),0 24px 60px rgba(29,33,31,.12);
  --sk-leaf:var(--radius-leaf,14px);
  font-family:var(--sk-sans);
  font-weight:400;
  color:var(--sk-ink);
  line-height:1.6;
  -webkit-font-smoothing:antialiased;
}
.sk *,.sk *::before,.sk *::after{box-sizing:border-box}
.sk-container{width:100%;max-width:var(--sk-max);margin-inline:auto;padding-inline:var(--sk-gutter)}
.sk-section{padding-block:56px}
@media (min-width:769px){.sk-section{padding-block:clamp(80px,7vw,120px)}}
.sk-surface-white{background:var(--sk-white)}
.sk-surface-warm{background:var(--sk-warm)}
.sk-surface-sage-grey{background:var(--sk-sage-grey)}
.sk-heading{font-family:var(--sk-serif);font-weight:500;line-height:1.12;letter-spacing:-.01em;color:var(--sk-ink);margin:0;text-transform:none}
.sk-heading strong{font-weight:600}
.sk-heading em{font-style:italic;color:var(--sk-sage)}
.sk-h1{font-size:clamp(2.3rem,1.5rem + 3vw,4.1rem)}
.sk-h2{font-size:clamp(1.8rem,1.3rem + 1.9vw,2.7rem)}
.sk-h3{font-size:clamp(1.25rem,1.15rem + .4vw,1.4rem);font-weight:500}
.sk-h4{font-size:1.15rem;font-weight:500}
.sk-lead{font-size:clamp(1.0625rem,1rem + .25vw,1.1875rem);font-weight:400;line-height:1.65;color:var(--sk-muted);margin:0}
.sk-body{font-size:1rem;line-height:1.65;color:var(--sk-muted);margin:0}
.sk-eyebrow{display:block;font-size:.75rem;font-weight:700;line-height:1.2;letter-spacing:.16em;color:var(--sk-brass-text);margin:0 0 .75rem;text-transform:uppercase}
.sk-section-head{display:grid;gap:1rem;max-width:44rem;margin-bottom:clamp(2rem,1.5rem + 2vw,3.5rem)}
.sk-section-head--centre{margin-inline:auto;text-align:center}
.sk-btn{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;min-height:44px;padding:.8em 1.35em;border-radius:var(--sk-radius-button);border:1px solid transparent;font-family:var(--sk-sans);font-size:.95rem;font-weight:700;line-height:1.3;text-decoration:none;white-space:nowrap;cursor:pointer;transition:background-color .15s ease,border-color .15s ease,color .15s ease,transform .15s ease,box-shadow .15s ease}
.sk-btn:hover{transform:translateY(-1px)}
.sk-btn--primary{background:var(--sk-sage);color:#fff;box-shadow:0 6px 18px rgba(46,74,62,.25)}
.sk-btn--primary:hover{background:var(--sk-sage-deep);color:#fff}
.sk-btn--secondary{background:var(--sk-white);border-color:var(--sk-stone-deep);color:var(--sk-ink)}
.sk-btn--secondary:hover{border-color:var(--sk-ink);color:var(--sk-ink)}
.sk-btn--light{background:var(--sk-white);color:var(--sk-ink)}
.sk-btn--light:hover{background:var(--sk-warm)}
.sk-btn--outline{background:transparent;border-color:var(--sk-ink);color:var(--sk-ink)}
.sk-btn--outline:hover{background:var(--sk-ink);color:#fff}
.sk-btn--brass{background:var(--sk-sand);color:var(--sk-ink)}
.sk-btn--brass:hover{background:var(--sk-brass-soft)}
.sk-btn--lg{padding:1.05em 1.7em;font-size:1rem}
.sk-btn--sm{min-height:36px;padding:.5em 1.05em;font-size:.85rem}
.sk-card{background:var(--sk-white);border:1px solid var(--sk-border);border-radius:var(--sk-radius-card)}
.sk-link-arrow{display:inline-flex;align-items:center;gap:.4em;font-weight:700;color:var(--sk-sage);text-decoration:none}
.sk-link-arrow::after{content:'→';transition:transform .15s}
.sk-link-arrow:hover::after{transform:translateX(3px)}
@media (prefers-reduced-motion:reduce){.sk-btn:hover{transform:none}}
.sk-pill{display:inline-flex;align-items:center;gap:.375rem;border-radius:999px;padding:4px 12px;font-size:.8125rem;font-weight:500;background:var(--sk-sage-grey);color:var(--sk-ink)}
.sk-textlink{display:inline-flex;align-items:center;gap:.5rem;color:var(--sk-ink);font-weight:500;text-decoration:underline;text-decoration-thickness:1px;text-underline-offset:.35em}
.sk-textlink:hover{color:var(--sk-sage-deep)}
.sk-textlink svg{flex:none;transition:transform .2s ease}
.sk-textlink:hover svg{transform:translateX(3px)}
.sk-leaf{border-radius:var(--sk-leaf);overflow:hidden}
.sk-img{display:block;width:100%;height:100%;object-fit:cover}
.sk-media{position:relative;background:var(--sk-stone);overflow:hidden}
.sk a:focus-visible,.sk button:focus-visible,.sk input:focus-visible,.sk summary:focus-visible,.sk select:focus-visible,.sk textarea:focus-visible,.sk [tabindex]:focus-visible{outline:2px solid var(--sk-focus);outline-offset:3px}
.sk-visually-hidden{position:absolute!important;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0}
.sk-list-reset{list-style:none;margin:0;padding:0}
@media(max-width:639px){.sk input,.sk select,.sk textarea{font-size:16px!important}}
@media (prefers-reduced-motion:reduce){.sk *,.sk *::before,.sk *::after{transition:none!important;animation:none!important;scroll-behavior:auto!important}}
`;

/** The base layer, emitted once per document. Every block renders it. */
export function SkBaseStyles() {
  return <SkStyles id="base" css={BASE_CSS} />;
}
